import { useLanguage } from "@/iti-core-react/hooks/useLanguage";
import { ItiMessageService } from "@/iti-core-react/services/iti-message-service";
import { PersonalNotificationDto } from "@/shared/dtos/personal-notifications-dto";
import { NotificationService } from "@/shared/services/notificationService";
import { ReactNode, createContext, useEffect, useState } from "react";
import "./NotificationContext.scss";

interface NotificationContextType {
	notifications: PersonalNotificationDto[];
	newNotificationsCount: number;
	getNewNotifications: () => void;
	resetNotificationsCount: () => void;
	freshIds: Set<string>; // IDs de notificaciones "recién llegadas"
	markAllSeen: () => void;
}

export const NotificationContext = createContext<NotificationContextType>({
	notifications: [],
	newNotificationsCount: 0,
	getNewNotifications: () => {},
	resetNotificationsCount: () => {},
	freshIds: new Set<string>(),
	markAllSeen: () => {},
});

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
	const { t } = useLanguage();
	const [notifications, setNotifications] = useState<PersonalNotificationDto[]>(
		[],
	);
	const [newNotificationsCount, setNewNotificationsCount] = useState(0);
	const [freshIds, setFreshIds] = useState<Set<string>>(new Set());
	const notificationInterval =
		Number.parseInt(import.meta.env.VITE_NOTIFICATION_INTERVAL, 10) || 10000;

	const MAX_NOTIFICATIONS = 4;

	const sortDesc = (a: PersonalNotificationDto, b: PersonalNotificationDto) => {
		const da = +new Date(a.generationDate ?? 0);
		const db = +new Date(b.generationDate ?? 0);
		if (db !== da) return db - da;
		return (b.id || "").localeCompare(a.id || "");
	};

	const dedupeById = (arr: PersonalNotificationDto[]) => {
		const map = new Map<string, PersonalNotificationDto>();
		for (const n of arr) map.set(n.id, n);
		return [...map.values()];
	};

	const getNewNotifications = async () => {
		const newNotifications =
			await NotificationService.getPersonalNewNotifications();
		if (newNotifications.length > 0) {
			// 1) Actualizar la lista global de notificaciones (para el panel)
			setNotifications((prev) => {
				const merged = dedupeById([...newNotifications, ...prev]);
				return merged.sort(sortDesc);
			});

			// 2) Actualizar contador
			setNewNotificationsCount((prev) => prev + newNotifications.length);

			// 3) Marcar estas como “recién llegadas” para el panel (azul)
			setFreshIds((prev) => {
				const next = new Set(prev);
				for (const n of newNotifications) {
					if (n?.id) next.add(n.id);
				}
				return next;
			});

			// 4) Toasts
			const notificationsToShow = newNotifications.slice(0, MAX_NOTIFICATIONS);
			const hasMoreNotifications = newNotifications.length > MAX_NOTIFICATIONS;

			ItiMessageService.toastNotificationsMultiple(
				notificationsToShow.map((notif: PersonalNotificationDto) => ({
					message: notif.message,
					type: "info",
				})),
			);

			if (hasMoreNotifications) {
				ItiMessageService.toastNotificationsMultiple([
					{
						message: `
            <div class="d-flex align-items-center">
              <i class="mdi mdi-bell-plus"></i>
              <span class="ms-2">
                <strong>
                  ${t("notification.pending-notifications", {
										count: newNotifications.length - MAX_NOTIFICATIONS,
									})}
                </strong>
              </span>
            </div>`,
						type: "info",
						cssClass: "toast-notification-more",
					},
				]);
			}
		}
	};

	const resetNotificationsCount = () => {
		setNewNotificationsCount(0);
	};

	// Cuando se entra en la vista de lista de notificaciones, limpian los “recién llegados”
	const markAllSeen = () => {
		setFreshIds(new Set());
	};

	useEffect(() => {
		// Primer fetch al montar → cargar historial reciente para rellenar el panel
		(async () => {
			const resp =
				await NotificationService.getPaginatedPersonalNotificationsWithMultiSort(
					0,
					5,
					["generationDate,DESC"],
					true,
					false,
				);
			setNotifications(dedupeById(resp.content || []).sort(sortDesc));
		})();
	}, []);

	useEffect(() => {
		const intervalId = setInterval(getNewNotifications, notificationInterval);
		return () => clearInterval(intervalId);
	}, []);

	return (
		<NotificationContext.Provider
			value={{
				notifications,
				newNotificationsCount,
				getNewNotifications,
				resetNotificationsCount,
				freshIds,
				markAllSeen,
			}}
		>
			{children}
		</NotificationContext.Provider>
	);
};
